// Apps: the contract every Bloom app ships (manifest, actions, collections, cards) and the helpers both halves share
// (input validation, command parsing and matching). See docs/apps.md.
//
// An app belongs to a server. Its manifest says what it is, which slash commands it has, what data it keeps
// (collections) and which named operations (actions) may change that data. Nothing writes an app's data except its
// actions, run by the room (worker/src/apps/); every write is logged as an event.
import type { Role } from "./protocol.ts";
import type { BotSpec } from "./bots.ts";

// ---- Manifest ----

/**
 * Kinds of input a command or action takes. "record": the id of one of the app's records (in `collection`). "media":
 * pictures and videos the caller uploaded (see FileRef).
 */
export type InputType = "text" | "number" | "choice" | "datetime" | "member" | "channel" | "record" | "media";

// ---- Hues ----

/**
 * Named hues: categorical colours (a category's pill, a chart's bars) and an app's own accent (`look.accent`). Like
 * tones, apps only name them: every theme defines `--hue-<name>` (text and marks), `-soft` (a pill's fill), `-fill`
 * and `-fill-hover` (filled buttons), worked out from its own surfaces to meet AA (scripts/gen-hues.mjs). Hues carry
 * no meaning (green isn't "good"): use the semantic tones for news.
 */
export type Hue = "red" | "orange" | "amber" | "green" | "teal" | "blue" | "violet" | "pink";
export const HUES: readonly Hue[] = ["red", "orange", "amber", "green", "teal", "blue", "violet", "pink"];
export const isHue = (v: unknown): v is Hue => typeof v === "string" && (HUES as readonly string[]).includes(v);

// ---- Media: pictures and videos attached to records ----

/** What a "media" input or field may hold. */
export type MediaKind = "image" | "video";
export const MEDIA_KINDS: readonly MediaKind[] = ["image", "video"];
/** Files a media input or field holds by default, and at most. */
export const DEFAULT_MAX_FILES = 4;
export const MAX_FILES = 10;
const MEDIA_TYPE: Record<MediaKind, RegExp> = { image: /^image\/(png|jpeg|gif|webp|avif)$/, video: /^video\/(mp4|webm|quicktime)$/ };
/** "image", "video", or null for a type no media field takes. */
export function mediaKindOf(type: string): MediaKind | null {
  return MEDIA_KINDS.find((k) => MEDIA_TYPE[k].test(type)) ?? null;
}
/** Does a media input or field (its `accept`, default both) take a file of this type? */
export function mediaAccepts(spec: { accept?: MediaKind[] }, type: string): boolean {
  const k = mediaKindOf(type);
  return !!k && (spec.accept?.length ? spec.accept : MEDIA_KINDS).includes(k);
}
/** "pictures", "videos" or "pictures and videos": what a media input takes, for messages. */
export const mediaWords = (spec: { accept?: MediaKind[] }) => {
  const a = spec.accept?.length ? spec.accept : MEDIA_KINDS;
  return a.length === 2 ? "pictures and videos" : a[0] === "video" ? "videos" : "pictures";
};

/**
 * One uploaded file in an app record's media field. The platform writes it (from the room's own record of the upload,
 * never from what a client or an app says), so name, type and size are trustworthy. `key` is the file's storage key:
 * served only to the server's members, through Bloom's file route (a sandboxed view gets the bytes through the SDK).
 * The file belongs to that record from then on: dropping it from the record, or deleting the record, trashes it (a
 * restore within 30 days brings it back). AIs never see the key (worker/src/apps/tools.ts).
 */
export interface FileRef {
  key: string;
  name: string;
  type: string;
  size: number;
  width?: number;
  height?: number;
}

/** Is `v` shaped like a FileRef (not whether the file exists)? */
export function isFileRef(v: unknown): v is FileRef {
  if (!v || typeof v !== "object" || Array.isArray(v)) return false;
  const r = v as Record<string, unknown>;
  return typeof r.key === "string" && typeof r.name === "string" && typeof r.type === "string" && typeof r.size === "number";
}

/** The file refs a media value holds (anything else in it is skipped). */
export const fileRefs = (v: unknown): FileRef[] => (Array.isArray(v) ? v.filter(isFileRef) : []);

/** The media fields of one of an app's collections. */
export function mediaFields(app: Pick<AppManifest, "collections">, collection: string): string[] {
  const c = Object.hasOwn(app.collections, collection) ? app.collections[collection] : undefined;
  return c ? Object.entries(c.fields).filter(([, f]) => f.type === "media").map(([k]) => k) : [];
}

export interface InputSpec {
  key: string;
  type: InputType;
  /** Shown on forms ("Title"). Defaults to the key, capitalised. */
  label?: string;
  required?: boolean;
  /** "choice": the allowed values, in order. */
  options?: string[];
  /** Used when the input is missing (and pre-filled on forms). */
  default?: string | number;
  /** "record": which collection the id points into. */
  collection?: string;
  /** "text": a multi-line box on forms. */
  multiline?: boolean;
  /** "text": most characters (default 500). "number": min/max. */
  maxLength?: number;
  min?: number;
  max?: number;
  placeholder?: string;
  /** "record" inputs on a form: the records to pick from (filled in by the room when it makes the form). */
  choices?: { value: string; label: string }[];
  /** "media": what it takes (default pictures and videos), and how many files (default 4, at most 10). */
  accept?: MediaKind[];
  maxFiles?: number;
}

/**
 * A field of a collection's records. Types as for inputs; "members": a list of member ids (votes, RSVPs); "media": a
 * list of FileRefs (pictures and videos people attached).
 */
export interface FieldSpec {
  type: InputType | "members";
  description?: string;
  required?: boolean;
  options?: string[];
  maxLength?: number;
  /** "media": what it holds (default pictures and videos), and how many files (default 4, at most 10). */
  accept?: MediaKind[];
  maxFiles?: number;
  /** What records without this field read as (a field added later: "status" reads as "Open"). */
  default?: string | number;
}

export interface CollectionSpec {
  description: string;
  fields: Record<string, FieldSpec>;
}

export interface ActionSpec {
  description: string;
  inputs: InputSpec[];
  /** Server roles allowed to call it (default: everyone in the server). */
  roles?: Role[];
  /**
   * Destructive or bulk (deleting data, changing many records): when an AI calls it (@Bloom, or an outside AI through
   * the app tools), it doesn't run; the person it acts for gets a private confirm card and it runs on their tap.
   */
  confirm?: boolean;
}

export type CardKind = "action" | "data" | "result";

export interface CommandSpec {
  /** `/<app> <name>`. */
  name: string;
  description: string;
  /** What it does: run (or ask for the inputs of) an action… */
  action?: string;
  /** …or post a data card from a view (see AppManifest.views). */
  view?: string;
  /**
   * The card it answers with. "action": always a form (an action card), shown only to the caller when `private`;
   * "result": runs straight away when the required inputs are there (otherwise the form); "data": a data card.
   */
  card: CardKind;
  /** Action cards: only the caller sees them ("Only you can see this"). */
  private?: boolean;
  /** Which input free text after the command fills ("/fr Add themes" → title = "Add themes"). */
  freeText?: string;
  /** Its form's heading and button ("Add request"). Default: the action's description, "Submit". */
  formTitle?: string;
  submit?: string;
  /** The inputs it asks for. Default: its action's inputs. */
  inputs?: InputSpec[];
}

/** A data view: a live list from one collection, drawn as a data card. */
export interface ViewSpec {
  title: string;
  collection: string;
  /** Field to sort by, descending; a list field ("members") sorts by its length. Default: newest first. */
  sortBy?: string;
  limit: number;
  /** Field shown as each row's text, and fields shown after it. */
  titleField: string;
  metaFields?: string[];
  /**
   * Meta fields drawn as pills instead of plain text, by field: a number format and a tone (one tone, a tone per value,
   * or "sign": above zero positive ▲, below zero negative ▼). `{ percent: { format: "percent", tone: "sign" },
   * status: { tone: { Done: "positive", "In progress": "accent" } } }`. See ViewPill.
   */
  pills?: Record<string, ViewPill>;
  /** A "members" field whose size is shown on the right (votes). */
  countField?: string;
  /** A number field shown on the right instead (scores, points), in a pill format ("money", "percent"…). */
  valueField?: string;
  valueFormat?: ViewPillFormat;
  /** Number the rows (a leaderboard): 🥇🥈🥉 for the first three, then 4, 5… */
  rank?: boolean;
  /** Only records whose field is one of these values (a missing field reads as its default): `{ status: ["Open", "In progress"] }`. */
  where?: Record<string, (string | number | boolean)[]>;
}

/** A view's rows: filtered by `where`, sorted like its data card (a members list by its size), then newest first. */
export function viewRows(records: AppRecord[], view: ViewSpec, collection?: CollectionSpec): AppRecord[] {
  const size = (v: unknown) => (Array.isArray(v) ? v.length : typeof v === "number" ? v : 0);
  const where = Object.entries(view.where ?? {});
  const value = (r: AppRecord, f: string) => r.data[f] ?? collection?.fields[f]?.default;
  return records
    .filter((r) => where.every(([f, vals]) => vals.includes(value(r, f) as string | number | boolean)))
    .sort((a, b) => (view.sortBy ? size(b.data[view.sortBy]) - size(a.data[view.sortBy]) : 0) || b.createdAt - a.createdAt)
    .slice(0, view.limit);
}

export interface AppManifest {
  /** `/<slug>`: lowercase letters, digits and dashes, unique in the server. */
  slug: string;
  name: string;
  /** One emoji. */
  icon: string;
  description: string;
  aliases?: string[];
  /** The command `/<slug> <text>` means when <text> doesn't start with a command name. */
  defaultCommand?: string;
  commands: CommandSpec[];
  collections: Record<string, CollectionSpec>;
  actions: Record<string, ActionSpec>;
  views?: Record<string, ViewSpec>;
  /** Written by us and run in-process (true), or generated (its actions run in a Dynamic Worker). */
  firstParty?: boolean;
  version: number;
  /** Standard exceptions the app declares (custom view, custom card, extra integration); marked custom on its page. */
  exceptions?: string[];
  /** Outside services (ids from the integrations catalog) the app reads from. It may call only these. */
  integrations?: string[];
  /** Actions Bloom runs on its own every few minutes (published apps only; docs/integrations.md). */
  schedules?: ScheduleSpec[];
  /** Generated apps: set by Bloom (never by the build agent). See docs/blooming.md. */
  bloom?: GeneratedInfo;
  /** A bot (docs/bots.md): its chat name and avatar, and what runs it. Bots may have no full view. */
  bot?: BotSpec;
  /** How the app looks (its accent hue, font, density, corners, width, header), in its full view and its cards. */
  look?: AppLook;
}

// ---- Look: an app's own style, within the theme ----

/**
 * An app's look. Every choice is a name the host maps to theme variables and the kit's styles, never a colour or CSS
 * from the app, so an app still follows light, dark and every named theme and keeps AA contrast.
 * - accent: one of the HUES in place of Bloom's magenta (buttons, links, the card stripe, "accent" pills).
 * - font: "default" (the device's own UI font), "rounded", "serif" or "mono" (the device's own faces: no downloads).
 * - density: "comfortable" (default) or "compact" (tighter rows and padding).
 * - corners: "default", "round" (softer, pill buttons) or "square" (crisp).
 * - width: the full view's column: "narrow" (560), "default" (880), "wide" (1200) or "full".
 * - header: "plain" (default) or "banner" (the page head on a wash of the accent, with the app's icon).
 */
export interface AppLook {
  accent?: Hue;
  font?: LookFont;
  density?: "comfortable" | "compact";
  corners?: "default" | "round" | "square";
  width?: "narrow" | "default" | "wide" | "full";
  header?: "plain" | "banner";
}
export type LookFont = "default" | "rounded" | "serif" | "mono";
export const LOOK_OPTIONS = {
  accent: HUES,
  font: ["default", "rounded", "serif", "mono"],
  density: ["comfortable", "compact"],
  corners: ["default", "round", "square"],
  width: ["narrow", "default", "wide", "full"],
  header: ["plain", "banner"],
} as const satisfies { [K in keyof Required<AppLook>]: readonly Required<AppLook>[K][] };

/** An app's look with only known choices (anything else dropped); undefined when nothing is left. */
export function cleanLook(v: unknown): AppLook | undefined {
  if (!v || typeof v !== "object" || Array.isArray(v)) return undefined;
  const src = v as Record<string, unknown>;
  const out: Record<string, string> = {};
  for (const [k, opts] of Object.entries(LOOK_OPTIONS)) {
    const x = src[k];
    if (typeof x === "string" && (opts as readonly string[]).includes(x)) out[k] = x;
  }
  return Object.keys(out).length ? (out as AppLook) : undefined;
}

/** What's wrong with a manifest's look, for the build checks. */
export function lookProblems(v: unknown): string[] {
  if (v === undefined) return [];
  if (!v || typeof v !== "object" || Array.isArray(v)) return ["look must be an object: { accent, font, density, corners, width, header }"];
  const errors: string[] = [];
  for (const [k, x] of Object.entries(v as Record<string, unknown>)) {
    const opts = Object.hasOwn(LOOK_OPTIONS, k) ? (LOOK_OPTIONS as Record<string, readonly string[]>)[k] : undefined;
    if (!opts) errors.push(`look has no "${k}" (it takes ${Object.keys(LOOK_OPTIONS).join(", ")})`);
    else if (typeof x !== "string" || !opts.includes(x)) errors.push(`look.${k} must be one of ${opts.join(", ")}${k === "accent" ? " (colours come from the theme, never the app)" : ""}`);
  }
  return errors;
}

/** An action run on a schedule, by Bloom on behalf of the app. The action takes no required inputs. */
export interface ScheduleSpec {
  action: string;
  /** How often: 5 to 1440 minutes. */
  everyMinutes: number;
}

export const MIN_SCHEDULE_MINUTES = 5;
export const MAX_SCHEDULES = 3;

/** What Bloom keeps about a generated app (or a draft of one). */
export interface GeneratedInfo {
  /** The bundle this manifest belongs to (its view and actions code, in R2). */
  bundle: string;
  /** The blooming thread that builds it (its build history). */
  thread: string;
  /** Whoever bloomed it: owns it (publish, roll back, permissions). */
  owner: string;
  /** People who may also prompt changes, publish and restore data (server owners and admins always may). */
  editors: string[];
  /** A preview of a draft (its data is a copy), not the live app. */
  draft?: boolean;
  publishedAt?: number;
  /** Bots: the bot's author id ("bot-<ULID>"), kept by its blooming thread so it never changes across versions. */
  botId?: string;
  /** The bundle has no full view (bots may have none): the app's page shows only its About tab. */
  noView?: boolean;
}

/** Slugs no app may take. */
export const RESERVED_SLUGS = ["bloom", "gif", "me", "help", "mute", "apps", "integrations"];

// ---- Data ----

/** One record of an app's collection. `data` is the app's JSON; the rest is kept by the platform. */
export interface AppRecord {
  id: string;
  collection: string;
  data: Record<string, unknown>;
  createdBy: string;
  createdAt: number;
  updatedBy: string;
  updatedAt: number;
}

/** One logged write. `before`/`after`: the record's data (null: didn't exist / was deleted). */
export interface AppEvent {
  id: number;
  app: string;
  at: number;
  userId: string;
  /** The action that wrote it, or "restore". */
  action: string;
  collection: string;
  recordId: string;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  /** Readable line, without the person: "added “Dark mode”". */
  summary: string;
  /** Restores: the event whose version was put back. */
  restoredFrom?: number;
  /** Made by an AI acting for `userId` ("Bloom"): History reads "Bloom on behalf of Sam". */
  via?: string;
}

// ---- Cards ----

export interface CardButton {
  label: string;
  /** "call": run `action` with `input` (the card's app). "open": open the app's full view (at `route`). */
  kind: "call" | "open";
  action?: string;
  input?: Record<string, unknown>;
  route?: Record<string, string>;
  primary?: boolean;
  /** A toggle bound to the card's live record: shows how many ids `field` holds, pressed when it holds mine. */
  toggleField?: string;
  /** Drawn in the danger colour (it deletes or ends something). */
  danger?: boolean;
  /** One emoji before the label. */
  icon?: string;
}

export interface CardForm {
  action: string;
  submit: string;
  fields: InputSpec[];
  /** Pre-filled values (from the command's text and the inputs' defaults). */
  values: Record<string, unknown>;
}

/**
 * A card: declarative JSON the host draws natively (never HTML from an app). Small and focused, one job each.
 * Text fields allow *emphasis*, **bold** and <@userId> mention tokens, nothing else.
 */
export interface Card {
  app: string;
  kind: CardKind;
  title?: string;
  /** Result cards: the line saying what happened ("<@id> added *Dark mode*"). */
  text?: string;
  /** Little facts after the text ("Theming", "P2"). */
  meta?: string[];
  /** Toned pills after the meta: statuses, changes ("Done" positive, "▲ 4.62%"). At most 4. */
  pills?: CardPill[];
  /** Stat tiles: a big value over a small label, in a grid (3 across, 2 on phones). At most 6. */
  stats?: CardStat[];
  /** Small muted line at the card's foot ("10197 sh · +$121,344.30 today · Updated just now"). */
  footnote?: string;
  /** The card's accent stripe (its left bar). Default: the app accent. */
  tone?: Tone;
  /** Label–value rows ("When" Sat 7pm, "Where" Joe's). At most 6. */
  fields?: CardField[];
  /** Progress bars ("Raised" 340 of 500). At most 5. */
  progress?: CardProgress[];
  /** A small bar or line chart (2–24 points). */
  chart?: CardChart;
  /** A short list: plain, ranked (a leaderboard: 🥇🥈🥉 4 5…) or a checklist. At most 8 items. */
  list?: CardList;
  /** A grid of small lettered tiles (a Wordle board, tic-tac-toe, a seating chart). Up to 8 rows of 10. */
  grid?: CardGrid;
  /** Faces of people (who's going): a members field of the bound record (live), or a fixed list of ids. */
  people?: CardPeople;
  /** How `media` is drawn: "thumbs" (default: a row of thumbnails) or "hero" (the first picture, large). */
  mediaLayout?: "thumbs" | "hero";
  /** Action cards: the form. */
  form?: CardForm;
  /** Live: the record this card shows (buttons with `toggleField` read it). */
  bind?: { collection: string; id: string };
  /** Data cards: a live list from a view. */
  view?: string;
  /**
   * Result cards with a bound record: one of its media fields, drawn as thumbnails (click to enlarge). Set it only when
   * the record has files: the card reserves room for them.
   */
  media?: string;
  buttons?: CardButton[];
  /** A used action card: what happened ("Added ✓"). */
  done?: { text: string; detail?: string };
  /** Set by the platform (never by an app): an AI (`via`, e.g. "Bloom") made this card, acting for `userId`. */
  onBehalfOf?: { via: string; userId: string };
  /**
   * Set by the platform: more results from this app in this channel that were folded into this card instead of
   * being posted (the per-channel rate limit, CARDS_PER_MINUTE).
   */
  more?: number;
  /**
   * Private confirm cards (set by the platform): an AI asked to run a destructive or bulk action (`confirm: true`)
   * for the card's owner. The action and input are kept here, so the tap runs exactly what was shown.
   */
  confirm?: { action: string; input: Record<string, unknown>; via: string; label: string };
}

/** Result cards one app may post per channel (and per thread) per minute; past that they fold into "+N more". */
export const CARDS_PER_MINUTE = 5;
export const CARDS_WINDOW_MS = 60_000;

/** A card only one person sees. Not a message: no seq, not in history, unread, search or push; one person's sockets only. */
export interface PrivateCard {
  id: string;
  /** Where it shows: a channel, DM or thread stream. */
  stream: string;
  app: string;
  card: Card;
  ts: number;
}

/**
 * Rough height a card takes in the message list (so the windowed list reserves room before it's drawn). `narrow`:
 * drawn at phone width (max-width 600px), where stat tiles go 2 across and are a little shorter.
 */
export function cardHeight(card: Card, views?: Record<string, ViewSpec>, opts: { narrow?: boolean } = {}): number {
  if (card.done) return 56;
  if (card.confirm) return 112;
  if (card.form) return 64 + card.form.fields.reduce((h, f) => h + (f.multiline ? 92 : 64), 0) + 52;
  if (card.view) return 92 + 34 * (views?.[card.view]?.limit ?? 5);
  const media = card.media ? (card.mediaLayout === "hero" ? (opts.narrow ? CARD_HERO_HEIGHT_NARROW : CARD_HERO_HEIGHT) + CARD_STAT_GAP : CARD_MEDIA_HEIGHT) : 0;
  return 104 + media + cardStatsHeight(card.stats?.length ?? 0, opts.narrow) + cardExtraBlocksHeight(card, opts.narrow) + (card.footnote ? CARD_FOOTNOTE_HEIGHT : 0);
}

/** Room a result card's thumbnails take (card.media). */
export const CARD_MEDIA_HEIGHT = 84;
/** A hero picture (mediaLayout "hero"): a fixed height, so the list reserves it before the picture loads. */
export const CARD_HERO_HEIGHT = 200;
export const CARD_HERO_HEIGHT_NARROW = 160;

/** Fixed sizes of the other blocks (px), so a card's height is known before it's drawn (web and iOS use them). */
export const CARD_BLOCK = {
  fieldRow: 22,
  progressRow: 34,
  chart: 96,
  caption: 20,
  listRow: 26,
  gridCell: 30,
  gridCellNarrow: 26,
  gridGap: 4,
  people: 32,
} as const;

/** Room the fields, progress bars, chart, list, grid and people take, each with the gap above it. */
export function cardExtraBlocksHeight(card: Pick<Card, "fields" | "progress" | "chart" | "list" | "grid" | "people">, narrow = false): number {
  const gap = CARD_STAT_GAP;
  let h = 0;
  if (card.fields?.length) h += gap + card.fields.length * CARD_BLOCK.fieldRow;
  if (card.progress?.length) h += gap + card.progress.length * CARD_BLOCK.progressRow;
  if (card.chart?.points?.length) h += gap + CARD_BLOCK.chart + (card.chart.caption ? CARD_BLOCK.caption : 0);
  if (card.list?.items?.length) h += gap + card.list.items.length * CARD_BLOCK.listRow;
  if (card.grid?.rows?.length) {
    const cell = narrow ? CARD_BLOCK.gridCellNarrow : CARD_BLOCK.gridCell;
    h += gap + card.grid.rows.length * (cell + CARD_BLOCK.gridGap) - CARD_BLOCK.gridGap + (card.grid.caption ? CARD_BLOCK.caption : 0);
  }
  if (card.people) h += gap + CARD_BLOCK.people;
  return h;
}

/** A progress bar's share (0–1) and its text: its own, "68%" out of 100, else "3/5". */
export function progressShare(p: Pick<CardProgress, "value" | "max" | "text">): { share: number; text: string } {
  const max = typeof p.max === "number" && p.max > 0 ? p.max : 100;
  const v = Number.isFinite(p.value) ? p.value : 0;
  const share = Math.min(1, Math.max(0, v / max));
  const num = (n: number) => (Number.isInteger(n) ? n.toLocaleString("en-US") : n.toLocaleString("en-US", { maximumFractionDigits: 1 }));
  return { share, text: p.text ?? (max === 100 ? `${Math.round(share * 100)}%` : `${num(v)}/${num(max)}`) };
}

/** 🥇🥈🥉, then "4.", "5."…: a ranked list's marks. */
export const rankMark = (i: number) => (["🥇", "🥈", "🥉"][i] ?? `${i + 1}.`);

// ---- Card blocks: tones, pills, stat tiles, footnotes ----

/**
 * Semantic tones. The host draws them from theme variables (never colours from an app): positive → --tone-positive
 * (green), negative → --tone-negative (red), warning → --tone-warning (amber), accent → --accent, neutral → --ink-2.
 * A tone is never the only signal: pills and tiles carry their text, and a trend adds ▲/▼ plus "up"/"down" for
 * screen readers.
 */
export type Tone = SemanticTone | Hue;
export type SemanticTone = "positive" | "negative" | "warning" | "accent" | "neutral";
export const SEMANTIC_TONES: readonly SemanticTone[] = ["positive", "negative", "warning", "accent", "neutral"];

export const TONES: readonly Tone[] = [...SEMANTIC_TONES, ...HUES];
export const isTone = (v: unknown): v is Tone => typeof v === "string" && (TONES as readonly string[]).includes(v);

/** Which way a number moved: drawn as ▲ or ▼ (and read as "up" or "down"). */
export type Trend = "up" | "down";
export const isTrend = (v: unknown): v is Trend => v === "up" || v === "down";

/** A toned pill on a result card ("Done", "▲ 4.62%"). */
export interface CardPill {
  text: string;
  tone?: Tone;
  trend?: Trend;
}

/** A stat tile: a big value over a small label ("$269.65" / "Price"). */
export interface CardStat {
  label: string;
  value: string;
  tone?: Tone;
  trend?: Trend;
}

/** A label–value row on a result card. */
export interface CardField {
  label: string;
  value: string;
}

/** A progress bar: `value` of `max` (default 100), with its own text ("340 of 500") or the default ("68%", "3/5"). */
export interface CardProgress {
  label: string;
  value: number;
  max?: number;
  text?: string;
  tone?: Tone;
}

/** A small chart: bars, or a line over its points. Values are drawn to scale from zero (bars) or min–max (line). */
export interface CardChart {
  type: "bar" | "line";
  points: { label: string; value: number; tone?: Tone }[];
  tone?: Tone;
  /** One line under it ("Last 7 days"). */
  caption?: string;
  /** How values read in labels and aloud. */
  format?: ViewPillFormat;
}

export interface CardList {
  /** "plain" (default), "ranked" (🥇🥈🥉 then numbers) or "check" (✓ for done items, ○ for the rest). */
  style?: "plain" | "ranked" | "check";
  items: CardListItem[];
}
export interface CardListItem {
  text: string;
  /** On the right ("12 pts"). */
  value?: string;
  done?: boolean;
  tone?: Tone;
}

/** Rows of tiles, each a letter, digit or emoji (at most 2 characters) with a tone (a Wordle board's greens and ambers). */
export interface CardGrid {
  rows: CardGridCell[][];
  caption?: string;
}
export interface CardGridCell {
  text?: string;
  tone?: Tone;
}

export interface CardPeople {
  /** "Going", "Voted". */
  label?: string;
  /** A "members" field of the card's bound record: drawn live. */
  field?: string;
  /** Or a fixed list of member ids. */
  ids?: string[];
}

/** How meta fields of a view are drawn as pills on its data card (ViewSpec.pills). */
export interface ViewPill {
  /** Numbers only: "money" ($1,234.50), "percent" (+4.62%), "signed" (+12), "number" (1,234). */
  format?: ViewPillFormat;
  /** One tone, a tone per value ("Done": "positive"), or "sign" (above zero positive ▲, below negative ▼). */
  tone?: Tone | "sign" | Record<string, Tone>;
}
export type ViewPillFormat = "money" | "percent" | "signed" | "number";
export const VIEW_PILL_FORMATS: readonly ViewPillFormat[] = ["money", "percent", "signed", "number"];

/** Limits on the blocks (the checks refuse more; sanitizeCard cuts to them). */
export const CARD_LIMITS = {
  pills: 4, pillText: 24, stats: 6, statLabel: 24, statValue: 20, footnote: 160,
  fields: 6, fieldLabel: 24, fieldValue: 60,
  progress: 5, progressLabel: 32, progressText: 20,
  chartPoints: 24, chartLabel: 12, caption: 60,
  listItems: 8, listText: 60, listValue: 20,
  gridRows: 8, gridCols: 10, gridText: 2,
  people: 50, peopleLabel: 24, peopleShown: 8,
} as const;

/** A stat tile's height (px), on wide screens and on phones; tiles are a fixed height so cards reserve their room. */
export const CARD_STAT_HEIGHT = 62;
export const CARD_STAT_HEIGHT_NARROW = 56;
export const CARD_STAT_GAP = 8;
/** The footnote's line (it may wrap to a second on phones). */
export const CARD_FOOTNOTE_HEIGHT = 26;

/**
 * How many tiles go across: up to 3 on wide screens (4 tiles make a 2 × 2), 2 on phones. The web app's CSS
 * (web/src/kit/blocks.css, `--cols` and `--cols-narrow`) and cardHeight both use this.
 */
export function statColumns(n: number, narrow = false): number {
  if (n <= 1) return 1;
  if (narrow) return 2;
  return n === 4 ? 2 : Math.min(n, 3);
}

/** Room a result card's tiles take, with the gap above them. */
export function cardStatsHeight(n: number, narrow = false): number {
  if (!n) return 0;
  const rows = Math.ceil(Math.min(n, CARD_LIMITS.stats) / statColumns(n, narrow));
  return rows * (narrow ? CARD_STAT_HEIGHT_NARROW : CARD_STAT_HEIGHT) + (rows - 1) * CARD_STAT_GAP + CARD_STAT_GAP;
}

const ARROW: Record<Trend, string> = { up: "▲", down: "▼" };
/** "▲ 4.62%": a value with its trend arrow, for plain text (push, search, @Bloom). */
export const withTrend = (text: string, trend?: Trend) => (trend && !/^[▲▼]/.test(text) ? `${ARROW[trend]} ${text}` : text);

/** One line of tile and pill text for a card's plain-text body: "Price $269.65 · Change ▲ 4.62%". */
export function cardBlocksText(card: Pick<Card, "stats" | "pills" | "footnote" | "fields" | "progress" | "chart" | "list" | "grid">): string {
  const ranked = card.list?.style === "ranked";
  const chart = card.chart?.points?.length ? card.chart : undefined;
  const last = chart?.points[chart.points.length - 1];
  const parts = [
    ...(card.pills ?? []).map((p) => withTrend(p.text, p.trend)),
    ...(card.stats ?? []).map((s) => `${s.label} ${withTrend(s.value, s.trend)}`),
    ...(card.fields ?? []).map((f) => `${f.label} ${f.value}`),
    ...(card.progress ?? []).map((p) => `${p.label} ${progressShare(p).text}`),
    ...(chart ? [`${chart.caption ? `${chart.caption}: ` : ""}${last!.label} ${formatViewValue(last!.value, chart.format)}`] : []),
    ...(card.list?.items ?? []).map((it, i) => `${ranked ? `${i + 1}. ` : card.list?.style === "check" ? (it.done ? "✓ " : "○ ") : ""}${it.text}${it.value ? ` ${it.value}` : ""}`),
    ...(card.grid?.caption ? [card.grid.caption] : []),
  ];
  return [parts.join(" · "), card.footnote ? stripCardMarkup(card.footnote) : ""].filter(Boolean).join(" · ");
}

/** Card text without its markup (*em*, **bold**, \ escapes); mention tokens stay (the room decodes them). */
export const stripCardMarkup = (s: string) => s.replace(/\\(.)/g, (_, c: string) => (c === "*" ? "" : c)).replace(/\*/g, "").replace(//g, "*");

/**
 * A card message's body: the app's plain text plus what its tiles, pills and footnote say (the parts it doesn't
 * already mention), so push, search and @Bloom read the numbers too.
 */
export function cardBody(card: Card, text: string): string {
  const extra = cardBlocksText(card);
  if (!extra) return text;
  const missing = extra.split(" · ").filter((p) => !text.includes(p));
  return missing.length ? `${text}${text ? " · " : ""}${missing.join(" · ")}` : text;
}

/** A string's user-perceived characters (🟩 and 👍🏽 are one each). */
export function graphemes(s: string): string[] {
  const Seg = (Intl as { Segmenter?: new () => { segment(s: string): Iterable<{ segment: string }> } }).Segmenter;
  return Seg ? [...new Seg().segment(s)].map((x) => x.segment) : [...s];
}

const cleanText = (v: unknown, max: number): string | null => {
  if (typeof v !== "string" && typeof v !== "number") return null;
  const s = String(v).replace(/[\u0000-\u001f\u007f]+/g, " ").replace(/\s+/g, " ").trim();
  return s ? s.slice(0, max) : null;
};

/**
 * The blocks of an app's card, cleaned for the host: known tones and trends only, plain strings at their limits,
 * anything else dropped (an app's colours, HTML or URLs have nowhere to go: text is drawn as text). Used by
 * sanitizeCard; `cardBlockProblems` is the strict version the build checks report.
 */
export type CardBlocks = Pick<Card, "pills" | "stats" | "footnote" | "tone" | "fields" | "progress" | "chart" | "list" | "grid" | "people" | "mediaLayout">;
export function cleanCardBlocks(c: Partial<Card>): CardBlocks {
  const out: CardBlocks = {};
  if (isTone(c.tone)) out.tone = c.tone;
  if (Array.isArray(c.pills)) {
    const pills = c.pills.flatMap((p): CardPill[] => {
      const text = p && typeof p === "object" ? cleanText(p.text, CARD_LIMITS.pillText) : null;
      return text ? [{ text, ...(isTone(p.tone) ? { tone: p.tone } : {}), ...(isTrend(p.trend) ? { trend: p.trend } : {}) }] : [];
    }).slice(0, CARD_LIMITS.pills);
    if (pills.length) out.pills = pills;
  }
  if (Array.isArray(c.stats)) {
    const stats = c.stats.flatMap((s): CardStat[] => {
      if (!s || typeof s !== "object") return [];
      const label = cleanText(s.label, CARD_LIMITS.statLabel);
      const value = cleanText(s.value, CARD_LIMITS.statValue);
      return label && value ? [{ label, value, ...(isTone(s.tone) ? { tone: s.tone } : {}), ...(isTrend(s.trend) ? { trend: s.trend } : {}) }] : [];
    }).slice(0, CARD_LIMITS.stats);
    if (stats.length) out.stats = stats;
  }
  const foot = cleanText(c.footnote, CARD_LIMITS.footnote);
  if (foot) out.footnote = foot;
  const L = CARD_LIMITS;
  const obj = (v: unknown): Record<string, unknown> | null => (v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : null);
  const num = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : typeof v === "string" && v.trim() && Number.isFinite(Number(v)) ? Number(v) : null);
  const toned = (v: unknown) => (isTone(v) ? { tone: v } : {});
  if (Array.isArray(c.fields)) {
    const fields = c.fields.flatMap((f): CardField[] => {
      const o = obj(f);
      const label = o && cleanText(o.label, L.fieldLabel);
      const value = o && cleanText(o.value, L.fieldValue);
      return label && value ? [{ label, value }] : [];
    }).slice(0, L.fields);
    if (fields.length) out.fields = fields;
  }
  if (Array.isArray(c.progress)) {
    const bars = c.progress.flatMap((p): CardProgress[] => {
      const o = obj(p);
      const label = o && cleanText(o.label, L.progressLabel);
      const value = o && num(o.value);
      if (!o || !label || value === null) return [];
      const max = num(o.max);
      const text = cleanText(o.text, L.progressText);
      return [{ label, value, ...(max !== null && max > 0 ? { max } : {}), ...(text ? { text } : {}), ...toned(o.tone) }];
    }).slice(0, L.progress);
    if (bars.length) out.progress = bars;
  }
  const ch = obj(c.chart);
  if (ch && Array.isArray(ch.points)) {
    const points = ch.points.flatMap((p): CardChart["points"] => {
      const o = obj(p);
      const value = o && num(o.value);
      if (!o || value === null) return [];
      return [{ label: cleanText(o.label, L.chartLabel) ?? "", value, ...toned(o.tone) }];
    }).slice(-L.chartPoints);
    if (points.length >= 2) {
      const caption = cleanText(ch.caption, L.caption);
      const format = VIEW_PILL_FORMATS.includes(ch.format as ViewPillFormat) ? (ch.format as ViewPillFormat) : undefined;
      out.chart = { type: ch.type === "line" ? "line" : "bar", points, ...toned(ch.tone), ...(caption ? { caption } : {}), ...(format ? { format } : {}) };
    }
  }
  const li = obj(c.list);
  if (li && Array.isArray(li.items)) {
    const items = li.items.flatMap((it): CardListItem[] => {
      const o = typeof it === "string" ? { text: it } : obj(it);
      const text = o && cleanText(o.text, L.listText);
      if (!o || !text) return [];
      const value = cleanText(o.value, L.listValue);
      return [{ text, ...(value ? { value } : {}), ...(o.done === true ? { done: true } : {}), ...toned(o.tone) }];
    }).slice(0, L.listItems);
    if (items.length) out.list = { ...(li.style === "ranked" || li.style === "check" ? { style: li.style } : {}), items };
  }
  const gr = obj(c.grid);
  if (gr && Array.isArray(gr.rows)) {
    const rows = gr.rows.filter(Array.isArray).slice(0, L.gridRows).map((row: unknown[]) =>
      row.slice(0, L.gridCols).map((cell): CardGridCell => {
        const o = typeof cell === "string" ? { text: cell } : obj(cell) ?? {};
        const text = cleanText(o.text, 8);
        // At most 2 characters (a letter, a digit, an emoji): counted in graphemes, so 🟩 or 👍🏽 is one.
        const short = text ? graphemes(text).slice(0, L.gridText).join("") : "";
        return { ...(short ? { text: short } : {}), ...toned(o.tone) };
      }),
    ).filter((r) => r.length);
    if (rows.length) {
      const caption = cleanText(gr.caption, L.caption);
      out.grid = { rows, ...(caption ? { caption } : {}) };
    }
  }
  const pe = obj(c.people);
  if (pe) {
    const label = cleanText(pe.label, L.peopleLabel);
    const field = typeof pe.field === "string" && /^[A-Za-z_][A-Za-z0-9_]{0,39}$/.test(pe.field) ? pe.field : undefined;
    const ids = Array.isArray(pe.ids) ? [...new Set(pe.ids.filter((x): x is string => typeof x === "string" && x.length > 0 && x.length <= 64))].slice(0, L.people) : undefined;
    if (field || ids) out.people = { ...(label ? { label } : {}), ...(field ? { field } : ids ? { ids } : {}) };
  }
  if (c.mediaLayout === "hero") out.mediaLayout = "hero";
  return out;
}

/** What's wrong with a card's blocks (empty: nothing). For the build checks, which tell the agent exactly what to fix. */
export function cardBlockProblems(c: Partial<Card>): string[] {
  const errors: string[] = [];
  const tone = (v: unknown, where: string) => {
    if (v !== undefined && !isTone(v)) errors.push(`${where} tone "${String(v)}" isn't one of ${TONES.join(", ")} (colours come from the theme, never the app)`);
  };
  const trend = (v: unknown, where: string) => {
    if (v !== undefined && !isTrend(v)) errors.push(`${where} trend must be "up" or "down"`);
  };
  const text = (v: unknown, max: number, where: string) => {
    if (typeof v !== "string" || !v.trim()) errors.push(`${where} needs text`);
    else if (v.length > max) errors.push(`${where} is at most ${max} characters`);
  };
  tone(c.tone, "the card's");
  if (c.pills !== undefined) {
    if (!Array.isArray(c.pills)) errors.push("pills must be a list of { text, tone, trend }");
    else {
      if (c.pills.length > CARD_LIMITS.pills) errors.push(`at most ${CARD_LIMITS.pills} pills on a card`);
      c.pills.forEach((p, i) => {
        if (!p || typeof p !== "object") return void errors.push(`pill ${i + 1} must be { text, tone, trend }`);
        text(p.text, CARD_LIMITS.pillText, `pill ${i + 1}`);
        tone(p.tone, `pill ${i + 1}`);
        trend(p.trend, `pill ${i + 1}`);
      });
    }
  }
  if (c.stats !== undefined) {
    if (!Array.isArray(c.stats)) errors.push("stats must be a list of { label, value, tone, trend }");
    else {
      if (c.stats.length > CARD_LIMITS.stats) errors.push(`at most ${CARD_LIMITS.stats} stat tiles on a card`);
      c.stats.forEach((s, i) => {
        if (!s || typeof s !== "object") return void errors.push(`stat ${i + 1} must be { label, value, tone, trend }`);
        text(s.label, CARD_LIMITS.statLabel, `stat ${i + 1}'s label`);
        if (typeof s.value === "number") s = { ...s, value: String(s.value) };
        text(s.value, CARD_LIMITS.statValue, `stat ${i + 1}'s value`);
        tone(s.tone, `stat ${i + 1}`);
        trend(s.trend, `stat ${i + 1}`);
      });
    }
  }
  if (c.footnote !== undefined) text(c.footnote, CARD_LIMITS.footnote, "the footnote");
  const L = CARD_LIMITS;
  const list = (v: unknown, name: string, shape: string, max: number): unknown[] | null => {
    if (v === undefined) return null;
    if (!Array.isArray(v)) return errors.push(`${name} must be a list of ${shape}`), null;
    if (v.length > max) errors.push(`at most ${max} ${name} on a card`);
    return v;
  };
  const isNum = (v: unknown) => typeof v === "number" && Number.isFinite(v);
  list(c.fields, "fields", "{ label, value }", L.fields)?.forEach((f, i) => {
    const o = f as CardField;
    if (!o || typeof o !== "object") return void errors.push(`field ${i + 1} must be { label, value }`);
    text(o.label, L.fieldLabel, `field ${i + 1}'s label`);
    text(typeof o.value === "number" ? String(o.value) : o.value, L.fieldValue, `field ${i + 1}'s value`);
  });
  list(c.progress, "progress bars", "{ label, value, max, text, tone }", L.progress)?.forEach((p, i) => {
    const o = p as CardProgress;
    if (!o || typeof o !== "object") return void errors.push(`progress bar ${i + 1} must be { label, value, max }`);
    text(o.label, L.progressLabel, `progress bar ${i + 1}'s label`);
    if (!isNum(o.value)) errors.push(`progress bar ${i + 1}'s value must be a number`);
    if (o.max !== undefined && !(isNum(o.max) && o.max > 0)) errors.push(`progress bar ${i + 1}'s max must be a number above 0`);
    if (o.text !== undefined) text(o.text, L.progressText, `progress bar ${i + 1}'s text`);
    tone(o.tone, `progress bar ${i + 1}`);
  });
  if (c.chart !== undefined) {
    const ch = c.chart;
    if (!ch || typeof ch !== "object") errors.push("chart must be { type, points, tone, caption, format }");
    else {
      if (ch.type !== "bar" && ch.type !== "line") errors.push(`chart type must be "bar" or "line"`);
      // Fewer than 2 points draw no chart (data decides that, so it isn't an error: the rest of the card still posts).
      if (!Array.isArray(ch.points)) errors.push("chart points must be a list: [{ label, value }] (charts with fewer than 2 aren't drawn)");
      else {
        if (ch.points.length > L.chartPoints) errors.push(`at most ${L.chartPoints} chart points`);
        ch.points.forEach((p, i) => {
          if (!p || typeof p !== "object" || !isNum(p.value)) errors.push(`chart point ${i + 1} needs a number value`);
          else {
            if (p.label !== undefined && (typeof p.label !== "string" || p.label.length > L.chartLabel)) errors.push(`chart point ${i + 1}'s label is text of at most ${L.chartLabel} characters`);
            tone(p.tone, `chart point ${i + 1}`);
          }
        });
      }
      tone(ch.tone, "the chart's");
      if (ch.caption !== undefined) text(ch.caption, L.caption, "the chart's caption");
      if (ch.format !== undefined && !VIEW_PILL_FORMATS.includes(ch.format)) errors.push(`chart format must be one of ${VIEW_PILL_FORMATS.join(", ")}`);
    }
  }
  if (c.list !== undefined) {
    const li = c.list;
    if (!li || typeof li !== "object" || !Array.isArray(li.items)) errors.push("list must be { style, items: [{ text, value, done, tone }] }");
    else {
      if (li.style !== undefined && !["plain", "ranked", "check"].includes(li.style)) errors.push(`list style must be "plain", "ranked" or "check"`);
      if (li.items.length > L.listItems) errors.push(`at most ${L.listItems} list items on a card`);
      li.items.forEach((it, i) => {
        if (typeof it === "string") return text(it, L.listText, `list item ${i + 1}`);
        if (!it || typeof it !== "object") return void errors.push(`list item ${i + 1} must be { text, value, done, tone }`);
        text(it.text, L.listText, `list item ${i + 1}`);
        if (it.value !== undefined) text(typeof it.value === "number" ? String(it.value) : it.value, L.listValue, `list item ${i + 1}'s value`);
        if (it.done !== undefined && typeof it.done !== "boolean") errors.push(`list item ${i + 1}'s done must be true or false`);
        tone(it.tone, `list item ${i + 1}`);
      });
    }
  }
  if (c.grid !== undefined) {
    const gr = c.grid;
    if (!gr || typeof gr !== "object" || !Array.isArray(gr.rows)) errors.push("grid must be { rows: [[{ text, tone }]], caption }");
    else {
      if (gr.rows.length > L.gridRows) errors.push(`at most ${L.gridRows} grid rows`);
      gr.rows.forEach((row, r) => {
        if (!Array.isArray(row)) return void errors.push(`grid row ${r + 1} must be a list of cells`);
        if (row.length > L.gridCols) errors.push(`grid row ${r + 1} has more than ${L.gridCols} cells`);
        row.forEach((cell, k) => {
          const o = typeof cell === "string" ? { text: cell } : cell;
          if (!o || typeof o !== "object") return void errors.push(`grid cell ${r + 1}.${k + 1} must be { text, tone }`);
          if (o.text !== undefined && (typeof o.text !== "string" || graphemes(o.text).length > L.gridText)) errors.push(`grid cell ${r + 1}.${k + 1} text is at most ${L.gridText} characters (a letter, a number, an emoji)`);
          tone(o.tone, `grid cell ${r + 1}.${k + 1}`);
        });
      });
      if (gr.caption !== undefined) text(gr.caption, L.caption, "the grid's caption");
    }
  }
  if (c.people !== undefined) {
    const pe = c.people;
    if (!pe || typeof pe !== "object") errors.push("people must be { label, field } or { label, ids }");
    else {
      if (!pe.field === !pe.ids) errors.push("people takes either field (a members field of the bound record) or ids (member ids), not both or neither");
      if (pe.ids !== undefined && (!Array.isArray(pe.ids) || pe.ids.some((x) => typeof x !== "string"))) errors.push("people ids must be a list of member ids");
      else if (pe.ids && pe.ids.length > L.people) errors.push(`at most ${L.people} people`);
      if (pe.label !== undefined) text(pe.label, L.peopleLabel, "the people label");
    }
  }
  if (c.mediaLayout !== undefined && c.mediaLayout !== "thumbs" && c.mediaLayout !== "hero") errors.push(`mediaLayout must be "thumbs" or "hero"`);
  return errors;
}

/** What's wrong with a view's pills (ViewSpec.pills), for the manifest checks. */
export function viewPillProblems(name: string, view: ViewSpec): string[] {
  if (view.pills === undefined) return [];
  if (!view.pills || typeof view.pills !== "object" || Array.isArray(view.pills)) return [`view "${name}" pills must be an object of field → { format, tone }`];
  const errors: string[] = [];
  for (const [f, p] of Object.entries(view.pills)) {
    if (!view.metaFields?.includes(f)) errors.push(`view "${name}" has pills for "${f}", which isn't one of its metaFields`);
    if (!p || typeof p !== "object") {
      errors.push(`view "${name}" pill "${f}" must be { format, tone }`);
      continue;
    }
    if (p.format !== undefined && !VIEW_PILL_FORMATS.includes(p.format)) errors.push(`view "${name}" pill "${f}" format must be one of ${VIEW_PILL_FORMATS.join(", ")}`);
    const t = p.tone;
    if (t !== undefined && t !== "sign" && !isTone(t)) {
      if (!t || typeof t !== "object" || Array.isArray(t)) errors.push(`view "${name}" pill "${f}" tone must be a tone, "sign", or { value: tone }`);
      else for (const [v, x] of Object.entries(t)) if (!isTone(x)) errors.push(`view "${name}" pill "${f}" tone for "${v}" isn't one of ${TONES.join(", ")}`);
    }
  }
  return errors;
}

/** A view's pills, cleaned (known formats and tones, at most 4 fields, 20 values each). */
export function cleanViewPills(view: ViewSpec): Record<string, ViewPill> | undefined {
  if (!view.pills || typeof view.pills !== "object") return undefined;
  const out: Record<string, ViewPill> = {};
  for (const [f, p] of Object.entries(view.pills).slice(0, 4)) {
    if (!view.metaFields?.includes(f) || !p || typeof p !== "object") continue;
    const pill: ViewPill = {};
    if (p.format && VIEW_PILL_FORMATS.includes(p.format)) pill.format = p.format;
    if (p.tone === "sign" || isTone(p.tone)) pill.tone = p.tone;
    else if (p.tone && typeof p.tone === "object") pill.tone = Object.fromEntries(Object.entries(p.tone).filter(([, x]) => isTone(x)).slice(0, 20).map(([v, x]) => [v.slice(0, 40), x]));
    out[f] = pill;
  }
  return Object.keys(out).length ? out : undefined;
}

const signOf = (v: unknown): number => {
  const n = typeof v === "number" ? v : typeof v === "string" ? parseFloat(v.replace(/^[+\s]+/, "").replace(/^[−–]/, "-").replace(/[$,%\s]/g, "")) : NaN;
  return Number.isFinite(n) ? Math.sign(n) : 0;
};

/** Writes a view's value with its pill's format ("money", "percent"…); text passes through. */
export function formatViewValue(v: unknown, format?: ViewPillFormat): string {
  if (typeof v !== "number" || !Number.isFinite(v) || !format) return v === undefined || v === null ? "" : String(v);
  const sign = v > 0 ? "+" : v < 0 ? "−" : "";
  const abs = Math.abs(v);
  if (format === "money") return `${v < 0 ? "−" : ""}$${abs.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (format === "percent") return `${sign}${abs.toFixed(2)}%`;
  if (format === "signed") return `${sign}${abs.toLocaleString("en-US")}`;
  return v.toLocaleString("en-US");
}

/** A view row's meta field as a pill (text, tone, trend), or null when the view doesn't draw that field as one. */
export function viewPill(view: ViewSpec, field: string, value: unknown): CardPill | null {
  const p = view.pills && Object.hasOwn(view.pills, field) ? view.pills[field] : undefined;
  if (!p || value === undefined || value === null || value === "") return null;
  const text = formatViewValue(value, p.format);
  if (p.tone === "sign") {
    const s = signOf(value);
    return { text, tone: s > 0 ? "positive" : s < 0 ? "negative" : "neutral", ...(s > 0 ? { trend: "up" as const } : s < 0 ? { trend: "down" as const } : {}) };
  }
  const tone = isTone(p.tone) ? p.tone : p.tone && typeof p.tone === "object" && Object.hasOwn(p.tone, String(value)) ? p.tone[String(value)] : undefined;
  return { text, ...(tone ? { tone } : {}) };
}

// ---- Validation ----

export interface ValidationContext {
  isMember?: (id: string) => boolean;
  isChannel?: (id: string) => boolean;
  recordExists?: (collection: string, id: string) => boolean;
  /**
   * "media" inputs: a file the caller may attach, as the FileRef to store (the room's own record of it), or why not.
   * Without it (in the browser) the keys pass through as they are.
   */
  mediaFile?: (key: string, spec: InputSpec) => FileRef | string;
}

export const inputLabel = (s: InputSpec) => s.label ?? s.key.charAt(0).toUpperCase() + s.key.slice(1);

/**
 * Checks input against the declared inputs: unknown keys are dropped, defaults filled in, types and limits checked.
 * Returns the clean values, or a message per input that's wrong.
 */
export function validateInput(specs: InputSpec[], input: unknown, vc: ValidationContext = {}): { ok: true; values: Record<string, unknown> } | { ok: false; errors: Record<string, string> } {
  const src = input && typeof input === "object" && !Array.isArray(input) ? (input as Record<string, unknown>) : {};
  const values: Record<string, unknown> = {};
  const errors: Record<string, string> = {};
  for (const s of specs) {
    let v = Object.hasOwn(src, s.key) ? src[s.key] : undefined;
    if (typeof v === "string") v = v.trim();
    if (v === "" || v === null) v = undefined;
    if (v === undefined && s.default !== undefined) v = s.default;
    const label = inputLabel(s);
    if (v === undefined) {
      if (s.required) errors[s.key] = `${label} is required.`;
      continue;
    }
    switch (s.type) {
      case "text": {
        if (typeof v !== "string") { errors[s.key] = `${label} should be text.`; break; }
        const max = s.maxLength ?? 500;
        if (v.length > max) { errors[s.key] = `${label} is at most ${max} characters.`; break; }
        values[s.key] = s.multiline ? v : v.replace(/\s+/g, " ");
        break;
      }
      case "number": {
        const n = typeof v === "number" ? v : typeof v === "string" ? Number(v) : NaN;
        if (!Number.isFinite(n)) { errors[s.key] = `${label} should be a number.`; break; }
        if (s.min !== undefined && n < s.min) { errors[s.key] = `${label} is at least ${s.min}.`; break; }
        if (s.max !== undefined && n > s.max) { errors[s.key] = `${label} is at most ${s.max}.`; break; }
        values[s.key] = n;
        break;
      }
      case "choice": {
        const hit = s.options?.find((o) => o.toLowerCase() === String(v).toLowerCase());
        if (!hit) { errors[s.key] = `${label} is one of ${(s.options ?? []).join(", ")}.`; break; }
        values[s.key] = hit;
        break;
      }
      case "datetime": {
        const t = typeof v === "number" ? v : Date.parse(String(v));
        if (!Number.isFinite(t)) { errors[s.key] = `${label} should be a date and time.`; break; }
        values[s.key] = t;
        break;
      }
      case "member":
      case "channel":
      case "record": {
        if (typeof v !== "string" || v.length > 64) { errors[s.key] = `Pick a ${label.toLowerCase()}.`; break; }
        const known =
          s.type === "member" ? vc.isMember?.(v) : s.type === "channel" ? vc.isChannel?.(v) : vc.recordExists?.(s.collection ?? "", v);
        if (known === false) { errors[s.key] = s.type === "record" ? `That ${label.toLowerCase()} doesn't exist.` : `Pick a ${label.toLowerCase()}.`; break; }
        values[s.key] = v;
        break;
      }
      case "media": {
        // A key, or a list of keys (or refs: a view may hand back what it showed). Stored as the room's FileRefs.
        const list = (Array.isArray(v) ? v : [v]).map((x) => (typeof x === "string" ? x : isFileRef(x) || (x && typeof x === "object" && typeof (x as FileRef).key === "string") ? (x as FileRef).key : null));
        if (list.some((x) => x === null || x.length > 300)) { errors[s.key] = `${label} should be files you uploaded.`; break; }
        const keys = [...new Set(list as string[])];
        if (!keys.length) {
          if (s.required) errors[s.key] = `${label} is required.`;
          break;
        }
        const max = Math.min(s.maxFiles ?? DEFAULT_MAX_FILES, MAX_FILES);
        if (keys.length > max) { errors[s.key] = `${label} takes at most ${max} file${max === 1 ? "" : "s"}.`; break; }
        if (!vc.mediaFile) { values[s.key] = keys; break; }
        const refs: FileRef[] = [];
        for (const k of keys) {
          const r = vc.mediaFile(k, s);
          if (typeof r === "string") { errors[s.key] = r; break; }
          refs.push(r);
        }
        if (!errors[s.key]) values[s.key] = refs;
        break;
      }
    }
  }
  return Object.keys(errors).length ? { ok: false, errors } : { ok: true, values };
}

/** The inputs a command asks for: its own, or its action's. */
export function commandInputs(app: AppManifest, cmd: CommandSpec): InputSpec[] {
  return cmd.inputs ?? (cmd.action ? (app.actions[cmd.action]?.inputs ?? []) : []);
}

// ---- Commands ----

export interface ParsedCommand {
  app: AppManifest;
  /** null: the bare app (`/fr`), which opens its full view. */
  command: CommandSpec | null;
  /** Inline `key:value` arguments and free text, by input key (unvalidated). */
  args: Record<string, string>;
}

/** Finds the app a slug or alias names. */
export function findApp(apps: AppManifest[], name: string): AppManifest | undefined {
  const n = name.toLowerCase();
  return apps.find((a) => a.slug === n) ?? apps.find((a) => a.aliases?.includes(n));
}

/**
 * Reads typed text as a command: `/<app> [command] [args]`. Args are `key:value` (or `key:"two words"`) for the
 * command's inputs; the rest is free text for the command's `freeText` input. Null: not a command of these apps.
 */
export function parseCommand(text: string, apps: AppManifest[]): ParsedCommand | null {
  const m = /^\/([a-z0-9-]+)(?:\s+([\s\S]*))?$/i.exec(text.trim());
  if (!m) return null;
  const app = findApp(apps, m[1]!);
  if (!app) return null;
  let rest = (m[2] ?? "").trim();
  if (!rest) return { app, command: null, args: {} };
  // Command names are lowercase and match exactly, so "/fr Add dark mode" is free text for the default command
  // (a title starting with "Add"), while "/fr add dark mode" names the command.
  const first = rest.split(/\s+/, 1)[0]!;
  let command = app.commands.find((c) => c.name === first);
  if (command) rest = rest.slice(first.length).trim();
  else command = app.commands.find((c) => c.name === app.defaultCommand);
  if (!command) return null;
  const inputs = commandInputs(app, command);
  const args: Record<string, string> = {};
  // key:value pairs for declared inputs; everything else is free text.
  const free = rest
    .replace(/(^|\s)([a-z][a-z0-9_]*):(?:"([^"]*)"|(\S+))/gi, (all, lead: string, key: string, quoted?: string, bare?: string) => {
      const spec = inputs.find((i) => i.key.toLowerCase() === key.toLowerCase());
      if (!spec) return all;
      args[spec.key] = quoted ?? bare ?? "";
      return lead;
    })
    .trim();
  if (free) {
    if (command.freeText) args[command.freeText] = free;
    else return { app, command, args: { ...args, _text: free } };
  }
  return { app, command, args };
}

/** One entry in the "/" menu (and later the + menu, mobile, and @Bloom's tools): a command, or a bare app. */
export interface CommandEntry {
  app: AppManifest;
  command: CommandSpec | null;
  /** "/fr add" or "/fr". */
  label: string;
  description: string;
}

/** Can someone with `role` use this command? (Its action's `roles`, if any; views are for everyone.) */
export function canRunCommand(app: AppManifest, cmd: CommandSpec, role?: Role): boolean {
  if (!role || !cmd.action) return true;
  const roles = Object.hasOwn(app.actions, cmd.action) ? app.actions[cmd.action]?.roles : undefined;
  return !roles || roles.includes(role);
}

/**
 * Every command of every installed app, plus each bare app (opens it). The one registry everything reads: the "/"
 * menu, the + menu, mobile, and the app tools AIs get (worker/src/apps/tools.ts). With `role`, only the commands
 * someone with that role can run (a command only shows up for people who can call its action).
 */
export function commandRegistry(apps: AppManifest[], role?: Role): CommandEntry[] {
  const out: CommandEntry[] = [];
  for (const app of apps) {
    out.push({ app, command: null, label: `/${app.slug}`, description: `Open ${app.name}` });
    for (const c of app.commands) if (canRunCommand(app, c, role)) out.push({ app, command: c, label: `/${app.slug} ${c.name}`, description: c.description });
  }
  return out;
}

/** Does picking this command need input first (so the + menu opens its form)? */
export function commandTakesInput(app: AppManifest, cmd: CommandSpec): boolean {
  return commandInputs(app, cmd).length > 0;
}

/**
 * Fuzzy match for the "/" menu. `query` is what follows "/": "fr", "fr a", "feat", "vote". App names and aliases
 * count most, then command names, then descriptions. `recent`: labels used lately, ranked first among equals.
 */
export function matchCommands(entries: CommandEntry[], query: string, recent: string[] = []): CommandEntry[] {
  const q = query.toLowerCase().trim();
  const [appQ = "", cmdQ] = q.split(/\s+/, 2);
  const subseq = (hay: string, needle: string) => {
    let i = 0;
    for (const ch of hay) if (ch === needle[i]) i++;
    return i === needle.length;
  };
  const scored = entries.map((e) => {
    const names = [e.app.slug, ...(e.app.aliases ?? [])];
    const appName = e.app.name.toLowerCase();
    let s = 0;
    if (!q) s = 1;
    else if (cmdQ !== undefined) {
      // "fr a": the app, then a command starting with "a".
      if (names.some((n) => n.startsWith(appQ)) && e.command && e.command.name.startsWith(cmdQ)) s = 10;
    } else {
      if (names.some((n) => n === appQ)) s = e.command ? 8 : 9;
      else if (names.some((n) => n.startsWith(appQ))) s = 7;
      else if (appName.split(/\s+/).some((w) => w.startsWith(appQ))) s = 6;
      else if (e.command?.name.startsWith(appQ)) s = 5;
      else if (e.description.toLowerCase().includes(appQ)) s = 3;
      else if (subseq(`${e.app.slug} ${e.command?.name ?? ""} ${appName}`, appQ)) s = 1;
    }
    const r = recent.indexOf(e.label);
    return { e, s: s + (s && r >= 0 ? 0.5 - r / 100 : 0) };
  });
  return scored.filter((x) => x.s > 0).sort((a, b) => b.s - a.s).map((x) => x.e);
}

/** A slash command that isn't valid on its own yet: why (for the menu's hint). */
export function missingRequired(app: AppManifest, cmd: CommandSpec, args: Record<string, unknown>): string[] {
  return commandInputs(app, cmd).filter((i) => i.required && i.default === undefined && !String(args[i.key] ?? "").trim()).map((i) => i.key);
}
